import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Col from 'react-bootstrap/Col';
import Modal from 'react-bootstrap/Modal';
import Row from 'react-bootstrap/Row';
import { SELECTION_EVENT, trackUserEvent } from 'userActivityMiddleware';
import withEnabledLocations from 'containers/withEnabledLocations';
import withSounds from 'containers/withSounds';
import A11ySvg from 'components/A11ySvg';
import AccountCreationModal from 'components/AccountCreationModal';
import TextButton from 'components/TextButton';
import { LOCATION_DATA, PROPERTY_CHICAGO_SOUTHLAND } from 'utils/constants';
import { determineRequiredAccountType } from 'utils/locations';
import { userShape } from 'utils/shapes';
import { ReactComponent as ModalCloseIcon } from 'resources/cross.svg';
import './LocationPreference.css';

const TextButtonWithSounds = withSounds({ mouseEnterSound: true })(TextButton);
const HIDE_MODAL_CLOSE_DURATION_MS = 333;

class LocationPreference extends Component {
  constructor(props) {
    super(props);
    this.state = {
      newSelectedPreference: null,
      requiredAccountType: null,
      clickedButton: null,
      hideButtons: false,
      hideModal: false
    };

    this.handleSelect = this.handleSelect.bind(this);

    this.clickAnimationTimeout = null;
    this.moveAnimationTimeout = null;
    this.triggerCloseTimeout = null;
  }

  componentWillUnmount() {
    clearTimeout(this.clickAnimationTimeout);
    clearTimeout(this.moveAnimationTimeout);
    clearTimeout(this.triggerCloseTimeout);
  }

  componentDidUpdate(prevProps) {
    if (this.props.show && !prevProps.show) {
      this.setState({
        newSelectedPreference: null,
        clickedButton: null,
        hideButtons: false,
        hideModal: false
      });
    }
  }

  handleSelect(location) {
    const requiredAccountType = determineRequiredAccountType(
      location,
      this.props.user
    );

    if (requiredAccountType && !this.props.availableLocations) {
      this.setState({
        newSelectedPreference: location,
        requiredAccountType: requiredAccountType
      });
    } else {
      trackUserEvent({
        activityType: SELECTION_EVENT,
        eventName: 'new-preferred-location-selected',
        eventValue: location,
        initiator: 'text-button location'
      });
      this.props.onSelectPreference && this.props.onSelectPreference(location);
      if (this.props.retainModal) return;
      this.props.onClose && this.props.onClose();
    }
  }

  finalizeSelection = location => {
    if (location && !this.state.clickedButton) {
      this.setState({
        clickedButton: location,
        hideButtons: true
      });
      this.props.onSelectPreference && this.props.onSelectPreference(location);
      this.props.onClose && this.props.onClose();
    }
  };

  handleClosePreferenceSwitch = () => {
    this.setState({ newSelectedPreference: null });
  };

  handleCloseLocationPreference = () => {
    if (this.props.currSelectedPreference) {
      this.setState({
        hideButtons: true
      });

      if (this.props.onClose) {
        clearTimeout(this.triggerCloseTimeout);
        this.triggerCloseTimeout = setTimeout(
          this.props.onClose,
          HIDE_MODAL_CLOSE_DURATION_MS
        );
      }
    }
  };

  getCloseBtn() {
    return (
      !!this.props.currSelectedPreference && (
        <TextButton
          onClick={this.handleCloseLocationPreference}
          className="modal-close-button"
        >
          <A11ySvg component={ModalCloseIcon} alt="Close" />
        </TextButton>
      )
    );
  }

  renderPreferenceSwitchModal = () => {
    const { newSelectedPreference, requiredAccountType } = this.state;

    if (newSelectedPreference && !!requiredAccountType) {
      return (
        <AccountCreationModal
          accountType={requiredAccountType}
          location={newSelectedPreference}
          onClose={this.handleClosePreferenceSwitch}
          onFinalizeSelection={this.finalizeSelection}
        />
      );
    }
  };

  render() {
    const {
      show,
      backdrop,
      availableLocations,
      chicagoPropertySwitchEnabled,
      casinoReservation,
      currSelectedPreference,
      title,
      label
    } = this.props;

    const { hideModal } = this.state;
    const className = `modal-dialog-location-preference ${
      show && !hideModal ? 'preference-in' : 'preference-out'
    }`;
    const isHotelBooking = availableLocations && availableLocations.length > 0;
    const locationLabel = isHotelBooking
      ? casinoReservation
        ? 'Please select the Wind Creek location where you want to make a reservation!'
        : `Please select your preferred Wind Creek location to view that location's hotel booking!`
      : casinoReservation
      ? 'Please select the Wind Creek location where you want to make a reservation!'
      : `Please select your preferred Wind Creek location to view that location's WStore!`;

    const LOCATIONS = isHotelBooking
      ? availableLocations
      : this.props.enabledLocations;
    const chicagoSwitch =
      chicagoPropertySwitchEnabled?.remoteConfigEntry?.value;

    const locationButtons = LOCATIONS.map(location => {
      const locationObj = isHotelBooking ? LOCATION_DATA[location] : location;
      const className = `location${
        this.state.clickedButton === locationObj.id
          ? ' location-click'
          : this.state.hideButtons
          ? ' location-hide'
          : ''
      }`;

      const isChicagoProperty = locationObj.id === PROPERTY_CHICAGO_SOUTHLAND;
      const isChicagoPreferenceSelected =
        currSelectedPreference === PROPERTY_CHICAGO_SOUTHLAND;
      const disabled =
        !chicagoSwitch &&
        ((isChicagoPreferenceSelected && !isChicagoProperty) ||
          (!isChicagoPreferenceSelected && isChicagoProperty));

      return (
        !!locationObj &&
        (isHotelBooking ? (
          <Col sm={6} className={'location-col'} key={locationObj.id}>
            <TextButtonWithSounds
              className={`${className} img-col`}
              onClick={() => this.handleSelect(locationObj.id)}
              key={locationObj.id}
              disabled={disabled}
            >
              <img src={locationObj.image} alt={locationObj.name} />
              <span className="location-name"> {locationObj.name} </span>
            </TextButtonWithSounds>
          </Col>
        ) : (
          <TextButtonWithSounds
            className={className}
            onClick={() => this.handleSelect(locationObj.id)}
            key={locationObj.id}
            disabled={disabled}
          >
            <img src={locationObj.image} alt={locationObj.name} />
            <span className="location-name"> {locationObj.name} </span>
          </TextButtonWithSounds>
        ))
      );
    });

    return (
      <Modal
        className={className}
        onHide={this.handleCloseLocationPreference}
        show={show}
        backdrop={backdrop}
      >
        <Modal.Body>
          {this.getCloseBtn()}
          <div className="preference-title">
            {title || 'Select Your Location'}
          </div>
          <div className="preference-label">{label || locationLabel}</div>
          <div className="locations">
            {isHotelBooking ? <Row>{locationButtons}</Row> : locationButtons}
          </div>
          {!isHotelBooking && this.renderPreferenceSwitchModal()}
        </Modal.Body>
      </Modal>
    );
  }
}

LocationPreference.propTypes = {
  user: userShape,
  onClose: PropTypes.func,
  onSelectPreference: PropTypes.func,
  currSelectedPreference: PropTypes.string,
  show: PropTypes.bool,
  backdrop: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  availableLocations: PropTypes.array,
  title: PropTypes.string,
  label: PropTypes.string,
  retainModal: PropTypes.bool
};

export default withEnabledLocations(LocationPreference);
